/**
 * Ahirbhutamsha System Data (D40)
 * 8 AHI (Actions) × 5 BHUTA (Elements) = 40 divisions for mantra practice analysis
 * Based on Vedic astrology tradition
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number; // Parsed from position string
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 8 AHI types - actions/purposes of mantras
 */
export const AHI_TYPES = [
  'KRTA', 'KAVACHA', 'YATRA', 'DARSHANA',
  'SAMVRITI', 'BHESHAJA', 'PRASITI', 'NAYA'
] as const;

export type AhiType = typeof AHI_TYPES[number];

/**
 * 5 BHUTA types - elemental channels
 */
export const BHUTA_TYPES = [
  'PRITHVI', 'APAS', 'AGNI', 'VAYU', 'AKASHA'
] as const;

export type BhutaType = typeof BHUTA_TYPES[number];

/**
 * AHI descriptions and meanings
 */
export const AHI_DESCRIPTIONS: Record<AhiType, {
  name: string;
  sanskrit: string;
  description: string;
  degrees: string;
}> = {
  KRTA: {
    name: 'Creation',
    sanskrit: 'Krta',
    description: 'Creation, manifestation',
    degrees: '00°00\' - 03°45\''
  },
  KAVACHA: {
    name: 'Protection',
    sanskrit: 'Kavacha',
    description: 'Protection, armor',
    degrees: '03°45\' - 07°30\''
  },
  YATRA: {
    name: 'Movement',
    sanskrit: 'Yatra',
    description: 'Movement, travel',
    degrees: '07°30\' - 11°15\''
  },
  DARSHANA: {
    name: 'Discovery',
    sanskrit: 'Darshana',
    description: 'Discovery, search',
    degrees: '11°15\' - 15°00\''
  },
  SAMVRITI: {
    name: 'Concealment',
    sanskrit: 'Samvriti',
    description: 'Concealment, hiding',
    degrees: '15°00\' - 18°45\''
  },
  BHESHAJA: {
    name: 'Healing',
    sanskrit: 'Bheshaja',
    description: 'Healing, medicine',
    degrees: '18°45\' - 22°30\''
  },
  PRASITI: {
    name: 'Attack',
    sanskrit: 'Prasiti',
    description: 'Attack, destruction',
    degrees: '22°30\' - 26°15\''
  },
  NAYA: {
    name: 'Control',
    sanskrit: 'Naya',
    description: 'Control, management',
    degrees: '26°15\' - 30°00\''
  }
};

/**
 * BHUTA descriptions and meanings
 */
export const BHUTA_DESCRIPTIONS: Record<BhutaType, {
  name: string;
  sanskrit: string;
  quality: string;
}> = {
  PRITHVI: {
    name: 'Earth',
    sanskrit: 'Prithvi',
    quality: 'Stability, matter'
  },
  APAS: {
    name: 'Water',
    sanskrit: 'Apas',
    quality: 'Fluidity, emotions'
  },
  AGNI: {
    name: 'Fire',
    sanskrit: 'Agni',
    quality: 'Transformation, energy'
  },
  VAYU: {
    name: 'Air',
    sanskrit: 'Vayu',
    quality: 'Movement, intellect'
  },
  AKASHA: {
    name: 'Ether',
    sanskrit: 'Akasha',
    quality: 'Space, sound'
  }
};

/**
 * Ahirbhutamsha division attributes
 */
export interface AhirbhutamshaAttributes {
  planet: PlanetDisplay;
  positionInRashi: number; // 0°-30° position within rashi
  ahiNumber: number; // 1-8 (which AHI)
  ahiType: AhiType;
  bhutaNumber: number; // 1-5 (which BHUTA)
  bhutaType: BhutaType;
  amshaNumber: number; // 1-40 (final division number)
  interpretation: string;
}

/**
 * Complete Ahirbhutamsha data structure
 */
export interface AhirbhutamshaData {
  divisions: AhirbhutamshaAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Calculate AHI number from degrees in rashi
 * Formula: floor(degrees ÷ 3.75) + 1
 */
export function calculateAhiNumber(degreeInRashi: number): number {
  const ahiIndex = Math.floor(degreeInRashi / 3.75);
  return Math.min(ahiIndex + 1, 8); // Cap at 8
}

/**
 * Calculate BHUTA number from degrees in rashi
 * Formula: floor((degrees mod 3.75) ÷ 0.75) + 1
 */
export function calculateBhutaNumber(degreeInRashi: number): number {
  const remainderInAhi = degreeInRashi % 3.75;
  const bhutaIndex = Math.floor(remainderInAhi / 0.75);
  return Math.min(bhutaIndex + 1, 5); // Cap at 5
}

/**
 * Calculate Ahirbhutamsha number (1-40)
 * Formula: (AHI_number - 1) × 5 + BHUTA_number
 */
export function calculateAmshaNumber(ahiNumber: number, bhutaNumber: number): number {
  return (ahiNumber - 1) * 5 + bhutaNumber;
}

/**
 * Get AHI type from number
 */
export function getAhiType(ahiNumber: number): AhiType {
  return AHI_TYPES[ahiNumber - 1] || AHI_TYPES[0];
}

/**
 * Get BHUTA type from number
 */
export function getBhutaType(bhutaNumber: number): BhutaType {
  return BHUTA_TYPES[bhutaNumber - 1] || BHUTA_TYPES[0];
}

/**
 * Generate interpretation based on AHI and BHUTA combination
 */
export function getAhirbhutamshaInterpretation(ahiType: AhiType, bhutaType: BhutaType): string {
  const ahi = AHI_DESCRIPTIONS[ahiType];
  const bhuta = BHUTA_DESCRIPTIONS[bhutaType];

  return `${ahi.description} through ${bhuta.name.toLowerCase()} element`;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Ahirbhutamsha division for a planet
 */
export function calculatePlanetAhirbhutamsha(planet: PlanetDisplay): AhirbhutamshaAttributes {
  // Position within current rashi (0°-30°)
  const positionInRashi = planet.longitude % 30;

  // Calculate AHI and BHUTA
  const ahiNumber = calculateAhiNumber(positionInRashi);
  const bhutaNumber = calculateBhutaNumber(positionInRashi);

  // Get types
  const ahiType = getAhiType(ahiNumber);
  const bhutaType = getBhutaType(bhutaNumber);

  // Calculate final amsha number (1-40)
  const amshaNumber = calculateAmshaNumber(ahiNumber, bhutaNumber);

  // Generate interpretation
  const interpretation = getAhirbhutamshaInterpretation(ahiType, bhutaType);

  return {
    planet,
    positionInRashi,
    ahiNumber,
    ahiType,
    bhutaNumber,
    bhutaType,
    amshaNumber,
    interpretation
  };
}

/**
 * Calculate all Ahirbhutamsha divisions from API response
 */
export function calculateAhirbhutamshas(apiResponse: JatakaResponse): AhirbhutamshaData {
  if (!apiResponse?.graha_positions?.planets) {
    return { divisions: [] };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
  const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : undefined;

  // Convert API planets to PlanetDisplay format, INCLUDING Lagna, Rahu, Ketu
  const planets = apiResponse.graha_positions.planets
    .map(planet => convertToPlanetDisplay(planet, horaPlanetName));

  // Calculate Ahirbhutamsha for each planet
  const divisions = planets.map(planet => calculatePlanetAhirbhutamsha(planet));

  // Sort by amsha number for display
  divisions.sort((a, b) => a.amshaNumber - b.amshaNumber);

  return { divisions };
}
